import { useLanguage } from "../../contexts/LanguageContext";
import {
	formatSportLabel,
	selectableSports,
	toggleSportSelection,
} from "../../lib/utils";

/**
 * Selettore multiplo degli sport di un atleta: chip, il primo è il principale.
 *
 * È **uno solo** per l'atleta (Profilo) e per il coach (scheda atleta). Il coach
 * aveva una select singola che mandava `sport` al server, e salvare il peso di
 * un atleta CrossFit + IPSC gli toglieva IPSC senza che la select l'avesse mai
 * mostrato (B2 di docs/sport-config/ANALYSIS.md). Chi usa questo componente
 * manda sempre la lista intera come `sports`, mai `sport`.
 *
 * Le discipline già scelte ma assenti da `options` (disattivate dall'admin)
 * restano visibili come chip: nasconderle le renderebbe impossibili da togliere
 * pur continuando a viaggiare in ogni salvataggio. Per lo stesso motivo quelle
 * con `selectable: false` (disattivate, o sport personali di un coach fuori dal
 * proprio perimetro) si mostrano solo se già scelte: si tolgono, non si
 * propongono (docs/sport-config, Fase 3).
 *
 * Props:
 *  - options: [{ value, label, selectable? }] da `/sports`
 *  - value: array degli sport selezionati, il primo è il principale
 *  - onChange(nextArray)
 *  - disabled
 */
export function SportsPicker({ options = [], value = [], onChange, disabled = false }) {
	const { t } = useLanguage();
	const selected = Array.isArray(value) ? value.filter(Boolean) : [];
	const known = new Set(options.map((option) => option.value));
	const orphans = selected
		.filter((sport) => !known.has(sport))
		.map((sport) => ({ value: sport, label: formatSportLabel(sport, options) }));
	const chips = [...selectableSports(options, selected), ...orphans];

	if (chips.length === 0) {
		return (
			<p className="text-xs text-muted-foreground">{t("sportsNoneConfigured")}</p>
		);
	}

	return (
		<div className="flex flex-wrap gap-2">
			{chips.map((sport) => {
				const isSelected = selected.includes(sport.value);
				const isPrimary = selected[0] === sport.value;
				return (
					<button
						key={sport.value}
						type="button"
						disabled={disabled}
						aria-pressed={isSelected}
						onClick={() => onChange?.(toggleSportSelection(selected, sport.value))}
						className={`rounded-sm border px-3 py-2 text-sm transition-colors disabled:opacity-50 ${
							isSelected
								? "border-volt/60 bg-volt/15 text-volt"
								: "border-border text-muted-foreground hover:border-volt/40"
						}`}
					>
						{sport.label || formatSportLabel(sport.value, options)}
						{isPrimary && selected.length > 1 && (
							<span className="ml-1.5 text-[10px] uppercase tracking-wider opacity-70">
								{t("sportPrimaryBadge")}
							</span>
						)}
					</button>
				);
			})}
		</div>
	);
}
